import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAgentMemoryEnvironmentsQuery,
  useGetAgentMemoryWebsitesQuery,
  useGetAgentMemoryFilesQuery,
  useGetAgentMemoryFileQuery,
  useGetAgentMemoryFileVersionsQuery,
  useLazyGetAgentMemoryFileVersionQuery,
  usePutAgentMemoryFileMutation,
  setSelectedAgentMemoryEnv,
  selectSelectedAgentMemoryEnv,
} from "../../../app/api/console/agentMemoryApiSlice";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import EnvironmentSelector from "./EnvironmentSelector";
import MemoryMarkdown from "./MemoryMarkdown";
import { GROUP_DOCS, docForRel } from "./fileDocs";

const GROUP_DOT = {
  semantic: "bg-accent-purple",
  procedural: "bg-accent-green",
  episodic: "bg-severity-medium",
  config: "bg-accent-pink",
  runtime: "bg-text-tertiary",
  other: "bg-text-tertiary",
};

function stripUrl(url) {
  if (!url) return "";
  return url.replace(/^https?:\/\//, "").replace(/\/+$/, "");
}
// Readable label for a website: prefer the brand name from its mapping,
// then its site URL, and fall back to the S3 id.
function siteName(w) {
  return w.website_name || stripUrl(w.site_url) || w.id;
}

function fmtSize(b) {
  if (b == null) return "";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / 1024 / 1024).toFixed(2) + " MB";
}
function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("en-US", { day: "2-digit", month: "short" }) +
      " " +
      d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return iso;
  }
}

const DocIcon = () => (
  <svg
    className="h-4 w-4 text-text-tertiary flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
    />
  </svg>
);

function AgentMemory() {
  const dispatch = useDispatch();
  const selectedEnv = useSelector(selectSelectedAgentMemoryEnv);

  const [activeWebsite, setActiveWebsite] = useState(null);
  const [wsFilter, setWsFilter] = useState("");
  const [activeFile, setActiveFile] = useState(null);
  const [mode, setMode] = useState("preview"); // preview | edit
  const [editText, setEditText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(null); // null = current
  const [versionText, setVersionText] = useState(null);
  const [toast, setToast] = useState(null);

  const { data: environments = [], isLoading: envLoading } =
    useGetAgentMemoryEnvironmentsQuery();

  const { data: websitesData, isFetching: wsLoading } =
    useGetAgentMemoryWebsitesQuery(selectedEnv, { skip: !selectedEnv });
  const websites = websitesData?.websites || [];

  const { data: filesData, isFetching: filesLoading } =
    useGetAgentMemoryFilesQuery(
      { env: selectedEnv, website: activeWebsite },
      { skip: !selectedEnv || !activeWebsite }
    );

  // Use currentData (result for THIS key), not data (last success for any
  // args). RTK keeps `data` pointing at the previous file while the next key
  // is fetching or after it errors — that stale buffer + Save would overwrite
  // the newly selected S3 object with the prior file's contents. Same class as
  // Just-for-you FileEditor (PR #44).
  const {
    currentData: fileData,
    isFetching: fileLoading,
    isError: fileError,
    error: fileQueryError,
  } = useGetAgentMemoryFileQuery(
    { env: selectedEnv, key: activeFile?.key },
    { skip: !activeFile?.key || !activeFile?.viewable }
  );

  const { data: versionsData } = useGetAgentMemoryFileVersionsQuery(
    { env: selectedEnv, key: activeFile?.key },
    { skip: !activeFile?.key || !showVersions }
  );
  const [fetchVersion] = useLazyGetAgentMemoryFileVersionQuery();
  const [putFile, { isLoading: saving }] = usePutAgentMemoryFileMutation();

  const fileReady = Boolean(
    activeFile?.key && fileData && fileData.key === activeFile.key
  );

  // Default environment once the list loads.
  useEffect(() => {
    if (!selectedEnv && environments.length) {
      const fallback =
        environments.find((e) => e.env === "staging") || environments[0];
      dispatch(setSelectedAgentMemoryEnv(fallback.env));
    }
  }, [environments, selectedEnv, dispatch]);

  // Drop the buffer immediately on key change so a remount-less switch cannot
  // briefly expose (or save) the previous file's text under the new key.
  useEffect(() => {
    setEditText("");
    setDirty(false);
  }, [activeFile?.key]);

  // Sync editor text only when currentData matches the selected key.
  useEffect(() => {
    if (!fileReady) return;
    setEditText(fileData.text || "");
    setDirty(false);
  }, [fileReady, fileData]);

  // Auto-hide toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const guardDirty = (msg) => {
    if (dirty && !window.confirm(msg)) return false;
    return true;
  };

  const changeEnv = (env) => {
    if (!guardDirty("Unsaved changes. Switch environment?")) return;
    dispatch(setSelectedAgentMemoryEnv(env));
    setActiveWebsite(null);
    resetFile();
  };

  const selectWebsite = (id) => {
    if (!guardDirty("Unsaved changes. Switch website?")) return;
    setActiveWebsite(id);
    resetFile();
  };

  const resetFile = () => {
    setActiveFile(null);
    setMode("preview");
    setEditText("");
    setDirty(false);
    setShowVersions(false);
    setActiveVersionId(null);
    setVersionText(null);
  };

  const openFile = (f) => {
    if (!guardDirty("Unsaved changes. Open another file?")) return;
    setActiveFile(f);
    setMode("preview");
    setShowVersions(false);
    setActiveVersionId(null);
    setVersionText(null);
    setEditText("");
    setDirty(false);
  };

  const onEditChange = (e) => {
    setEditText(e.target.value);
    setDirty(true);
  };

  const confirmSave = async () => {
    setShowConfirm(false);
    if (!fileReady || !activeFile?.key) {
      setToast({
        kind: "err",
        msg: "Wait for this file to finish loading before saving.",
      });
      return;
    }
    try {
      await putFile({
        env: selectedEnv,
        key: activeFile.key,
        contents: editText,
        website: activeWebsite,
      }).unwrap();
      setDirty(false);
      setToast({ kind: "ok", msg: "Saved ✓ (previous version kept)" });
    } catch (err) {
      setToast({
        kind: "err",
        msg: "Failed: " + (err?.data?.detail || err?.error || "error"),
      });
    }
  };

  const viewVersion = async (v) => {
    if (v.is_latest || v.version_id == null) {
      setActiveVersionId(null);
      setVersionText(null);
      return;
    }
    try {
      const res = await fetchVersion({
        env: selectedEnv,
        key: activeFile.key,
        versionId: v.version_id,
      }).unwrap();
      setActiveVersionId(v.version_id);
      setVersionText(res.text || "");
      setMode("preview");
    } catch {
      setToast({ kind: "err", msg: "Could not load this version" });
    }
  };

  const isProd = environments.find((e) => e.env === selectedEnv)?.is_prod;
  const fileDoc = activeFile ? docForRel(activeFile.rel) : null;
  const previewText =
    activeVersionId != null
      ? versionText
      : dirty
        ? editText
        : fileReady
          ? fileData?.text
          : "";

  const filteredWebsites = websites.filter((w) => {
    const q = wsFilter.trim().toLowerCase();
    if (!q) return true;
    // Search every identifying field a website carries: S3 id, brand name,
    // site URL and backend website id.
    const haystack = [w.id, w.website_name, w.site_url, w.backend_website_id]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
  const activeWebsiteObj = websites.find((w) => w.id === activeWebsite);

  return (
    <div className="pb-4">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-h2 text-text-primary">Agent Memory</h1>
          <p className="text-body-sm text-text-secondary">
            Browse and edit the agentic memory stored on S3, by website and by
            environment.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnvironmentSelector
            environments={environments}
            value={selectedEnv}
            onChange={changeEnv}
            isLoading={envLoading}
          />
          <button
            onClick={() => setShowHelp(true)}
            className="px-3.5 py-2 text-[13px] font-medium text-text-secondary bg-white border border-border-default rounded-xl hover:bg-surface-neutral transition-colors"
          >
            ? Help
          </button>
        </div>
      </div>

      {/* 3-pane layout */}
      <div className="grid grid-cols-[260px_340px_1fr] gap-4 h-[calc(100vh-190px)]">
        {/* Websites */}
        <div className="bg-white border border-border-subtle rounded-2xl flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
            <span className="text-body-xs font-semibold uppercase tracking-wide text-text-tertiary">
              Websites
            </span>
            <span className="text-caption text-text-tertiary bg-surface-neutral px-2 py-0.5 rounded-full">
              {websites.length}
            </span>
          </div>
          <div className="p-2">
            <input
              value={wsFilter}
              onChange={(e) => setWsFilter(e.target.value)}
              placeholder="Filter…"
              className="w-full px-3 py-2 text-body-sm bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/30 placeholder:text-text-tertiary"
            />
          </div>
          <div className="flex-1 overflow-auto px-2 pb-2 scrollbar-thin min-h-0">
            {wsLoading ? (
              <p className="text-body-sm text-text-tertiary text-center py-6">
                Loading…
              </p>
            ) : filteredWebsites.length === 0 ? (
              <p className="text-body-sm text-text-tertiary text-center py-6">
                {selectedEnv ? "No website" : "Pick an environment"}
              </p>
            ) : (
              filteredWebsites.map((w) => (
                <button
                  key={w.id}
                  onClick={() => selectWebsite(w.id)}
                  data-testid={`agent-memory-site-${w.id}`}
                  title={
                    siteName(w) !== w.id ? `${siteName(w)} (${w.id})` : w.id
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-body-sm truncate transition-colors ${
                    activeWebsite === w.id
                      ? "bg-surface-neutral font-medium text-text-primary"
                      : "text-text-secondary hover:bg-surface-neutral"
                  }`}
                >
                  {siteName(w)}
                  {siteName(w) !== w.id && (
                    <span className="text-text-tertiary"> ({w.id})</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Files */}
        <div className="bg-white border border-border-subtle rounded-2xl flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-border-subtle">
            <span className="text-body-xs font-semibold uppercase tracking-wide text-text-tertiary">
              {activeWebsiteObj ? siteName(activeWebsiteObj) : "Memory files"}
            </span>
            {activeWebsiteObj && siteName(activeWebsiteObj) !== activeWebsite && (
              <div className="text-caption text-text-tertiary font-mono mt-0.5">
                {activeWebsite}
              </div>
            )}
          </div>
          <div className="flex-1 overflow-auto p-2 scrollbar-thin min-h-0">
            {!activeWebsite ? (
              <p className="text-body-sm text-text-tertiary text-center py-10">
                Select a website.
              </p>
            ) : filesLoading ? (
              <p className="text-body-sm text-text-tertiary text-center py-6">
                Loading…
              </p>
            ) : !filesData?.groups?.length ? (
              <p className="text-body-sm text-text-tertiary text-center py-6">
                No files
              </p>
            ) : (
              filesData.groups.map((g) => (
                <div key={g.id} className="mb-3">
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <span
                      className={`w-2 h-2 rounded-sm ${
                        GROUP_DOT[g.id] || "bg-text-tertiary"
                      }`}
                    />
                    <span className="text-body-xs font-semibold text-text-primary">
                      {g.label}
                    </span>
                    <span className="text-caption text-text-tertiary truncate">
                      {g.desc}
                    </span>
                  </div>
                  {g.files.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => openFile(f)}
                      data-testid={`agent-memory-file-${f.name}`}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors ${
                        activeFile?.key === f.key
                          ? "bg-surface-neutral"
                          : "hover:bg-surface-neutral"
                      }`}
                    >
                      <DocIcon />
                      <span className="flex-1 min-w-0 text-left">
                        <span className="block text-body-sm text-text-primary truncate">
                          {f.name}{" "}
                          {!f.viewable && (
                            <span title={f.readonly_reason}>🔒</span>
                          )}
                        </span>
                        <span className="block text-caption text-text-tertiary truncate">
                          {f.dir ? f.dir + "/ · " : ""}
                          {fmtDate(f.mtime)}
                        </span>
                      </span>
                      <span className="text-caption text-text-tertiary font-mono">
                        {fmtSize(f.size)}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Viewer / editor */}
        <div className="bg-white border border-border-subtle rounded-2xl flex flex-col min-h-0">
          {!activeFile ? (
            <div className="flex-1 flex items-center justify-center text-text-tertiary text-body-sm">
              Click a file to read or edit it.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-border-subtle flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-text-primary truncate">
                    {activeFile.name}
                  </div>
                  <div className="text-caption text-text-tertiary font-mono truncate">
                    {activeFile.key}
                  </div>
                </div>
                {activeFile.viewable && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex bg-surface-neutral rounded-lg p-0.5">
                      <button
                        onClick={() => {
                          setMode("preview");
                          setActiveVersionId(null);
                        }}
                        className={`px-3 py-1.5 text-body-xs font-medium rounded-md transition-colors ${
                          mode === "preview"
                            ? "bg-white shadow-sm text-text-primary"
                            : "text-text-secondary"
                        }`}
                      >
                        Preview
                      </button>
                      <button
                        disabled={
                          !activeFile.editable ||
                          activeVersionId != null ||
                          !fileReady
                        }
                        onClick={() => setMode("edit")}
                        data-testid="agent-memory-edit"
                        className={`px-3 py-1.5 text-body-xs font-medium rounded-md transition-colors disabled:opacity-40 ${
                          mode === "edit"
                            ? "bg-white shadow-sm text-text-primary"
                            : "text-text-secondary"
                        }`}
                      >
                        Edit
                      </button>
                    </div>
                    <button
                      onClick={() => setShowVersions((s) => !s)}
                      className={`px-3 py-1.5 text-body-xs font-medium rounded-lg border transition-colors ${
                        showVersions
                          ? "bg-surface-neutral border-border-default text-text-primary"
                          : "bg-white border-border-default text-text-secondary hover:bg-surface-neutral"
                      }`}
                    >
                      Versions
                    </button>
                    <button
                      disabled={!dirty || saving || !fileReady}
                      onClick={() => setShowConfirm(true)}
                      data-testid="agent-memory-save"
                      className="px-3.5 py-1.5 text-body-xs font-semibold text-white bg-button-primary rounded-lg hover:bg-button-primary-hover transition-colors disabled:opacity-40"
                    >
                      {saving ? "…" : "Save"}
                    </button>
                  </div>
                )}
              </div>

              {/* Per-file onboarding hint */}
              {fileDoc && (
                <div className="px-5 py-2.5 bg-surface-neutral/60 border-b border-border-subtle">
                  <span className="text-body-xs text-text-secondary">
                    <span className="font-semibold text-text-primary">
                      {fileDoc.title}
                    </span>{" "}
                    — {fileDoc.role}
                  </span>
                </div>
              )}

              {activeVersionId != null && (
                <div className="px-5 py-2 bg-severity-medium/10 border-b border-border-subtle text-body-xs text-severity-medium font-medium">
                  Viewing a previous version (read-only). Go back to “Preview”
                  for the current version.
                </div>
              )}

              <div className="flex-1 min-h-0 flex">
                <div className="flex-1 overflow-auto min-h-0">
                  {!activeFile.viewable ? (
                    <div className="m-5 p-4 rounded-xl bg-severity-medium/10 text-severity-medium text-body-sm">
                      🔒 Not viewable here —{" "}
                      {activeFile.readonly_reason || "non-text file"} (
                      {fmtSize(activeFile.size)}).
                    </div>
                  ) : fileLoading && !fileReady ? (
                    <p
                      className="text-body-sm text-text-tertiary text-center py-10"
                      data-testid="agent-memory-loading"
                    >
                      Loading…
                    </p>
                  ) : !fileReady ? (
                    <div
                      className="m-5 p-4 rounded-xl bg-red-50 text-red-600 text-body-sm"
                      data-testid="agent-memory-load-error"
                    >
                      {fileQueryError?.data?.detail ||
                        fileQueryError?.error ||
                        (fileError
                          ? "Could not load this file."
                          : "Waiting for this file to load…")}
                    </div>
                  ) : mode === "edit" ? (
                    <textarea
                      value={editText}
                      onChange={onEditChange}
                      spellCheck={false}
                      data-testid="agent-memory-editor"
                      className="w-full h-full p-5 font-mono text-body-xs leading-relaxed text-text-primary resize-none border-none focus:outline-none bg-surface-neutral/30"
                    />
                  ) : activeFile.ext === ".md" ? (
                    <div
                      className="px-6 py-5 max-w-3xl"
                      data-testid="agent-memory-ready"
                    >
                      <MemoryMarkdown text={previewText || ""} />
                    </div>
                  ) : (
                    <pre
                      className="px-6 py-5 font-mono text-body-xs text-text-primary whitespace-pre-wrap break-words"
                      data-testid="agent-memory-ready"
                    >
                      {previewText || ""}
                    </pre>
                  )}
                </div>

                {/* Versions side panel */}
                {showVersions && (
                  <div className="w-64 border-l border-border-subtle flex flex-col min-h-0">
                    <div className="px-4 py-2.5 border-b border-border-subtle text-body-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      Versions ({versionsData?.count ?? 0})
                    </div>
                    <div className="flex-1 overflow-auto p-2 scrollbar-thin min-h-0">
                      {!versionsData?.versions?.length ? (
                        <p className="text-body-xs text-text-tertiary text-center py-4">
                          No versions
                        </p>
                      ) : (
                        versionsData.versions.map((v) => {
                          const isActive =
                            (activeVersionId == null && v.is_latest) ||
                            activeVersionId === v.version_id;
                          return (
                            <button
                              key={v.version_id || "current"}
                              onClick={() => viewVersion(v)}
                              className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                                isActive
                                  ? "bg-surface-neutral"
                                  : "hover:bg-surface-neutral"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-body-xs font-medium text-text-primary">
                                  {fmtDate(v.last_modified)}
                                </span>
                                {v.is_latest && (
                                  <span className="text-caption font-semibold text-accent-green">
                                    current
                                  </span>
                                )}
                              </div>
                              <div className="text-caption text-text-tertiary">
                                {fmtSize(v.size)}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation before write */}
      <ConfirmationModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmSave}
        title={isProd ? "Write to PRODUCTION" : "Confirm save"}
        message={`You are about to overwrite “${activeFile?.key || ""}” in the ${
          selectedEnv || ""
        } environment${
          isProd ? " (PRODUCTION)" : ""
        }. The previous version is kept (S3 versioning). Continue?`}
        confirmText="Save"
        cancelText="Cancel"
        confirmColor={isProd ? "red" : "green"}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl shadow-lg text-body-sm z-50 border ${
            toast.kind === "ok"
              ? "bg-white border-accent-green text-text-primary"
              : "bg-white border-severity-high text-text-primary"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Help drawer */}
      {showHelp && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/25"
            onClick={() => setShowHelp(false)}
          />
          <div className="relative w-[460px] max-w-full h-full bg-white shadow-xl overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h3 text-text-primary">What is each file for?</h2>
              <button
                onClick={() => setShowHelp(false)}
                className="text-text-tertiary hover:text-text-primary"
              >
                ✕
              </button>
            </div>
            <p className="text-body-sm text-text-secondary mb-5">
              A website's memory is a set of small files, each with a specific
              role. Here's how to read them.
            </p>
            {Object.entries(GROUP_DOCS).map(([id, doc]) => (
              <div key={id} className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-2.5 h-2.5 rounded-sm ${
                      GROUP_DOT[id] || "bg-text-tertiary"
                    }`}
                  />
                  <h3 className="text-body font-semibold text-text-primary">
                    {doc.title}
                  </h3>
                </div>
                <p className="text-body-sm text-text-secondary">{doc.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentMemory;
